import { cloneElement, createContext, isValidElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uiMessages } from './uiMessages';
import { serviceMessages } from './serviceMessages';
import { contentMessages } from './contentMessages';
import { updateMessages } from './updateMessages';

export const languages = [{ code: 'en', label: 'English', short: 'EN' }, { code: 'fr', label: 'Français', short: 'FR' }, { code: 'es', label: 'Español', short: 'ES' }];
const normalize = text => text.replace(/\s+/g, ' ').replace(/[’‘]/g, "'").trim().toLowerCase();
const catalog = new Map([...uiMessages, ...serviceMessages, ...contentMessages, ...updateMessages].map(([en, fr, es]) => [normalize(en), { fr, es }]));
const Context = createContext(null);
const storageKey = 'techspitze-language';
const readLanguage = () => { try { const saved = localStorage.getItem(storageKey); return languages.some(l => l.code === saved) ? saved : 'en'; } catch { return 'en'; } };

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(readLanguage);
  const setLocale = useCallback(code => { if (languages.some(l => l.code === code)) setLocaleState(code); }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = 'ltr';
    try { localStorage.setItem(storageKey, locale); } catch { /* Language still works if browser storage is disabled. */ }
  }, [locale]);
  const t = useCallback(text => {
    if (typeof text !== 'string' || locale === 'en' || !text.trim()) return text;
    const translated = catalog.get(normalize(text))?.[locale];
    if (!translated) return text; // Proper names, addresses, technology brands and user input remain unchanged.
    const prefix = text.match(/^\s*/)[0], suffix = text.match(/\s*$/)[0];
    const uppercase = /[A-Z]/.test(text) && text === text.toUpperCase();
    return prefix + (uppercase ? translated.toLocaleUpperCase(locale) : translated) + suffix;
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useLocale = () => { const value = useContext(Context); if (!value) throw new Error('useLocale requires LocaleProvider'); return value; };

// Translate React children/visible copy BEFORE rendering. No DOM mutation, no
// remote translation, and no remount of forms. IDs, hrefs, values and keys stay stable.
const copyProps = ['aria-label', 'alt', 'placeholder', 'title', 'description', 'eyebrow'];
const translateNode = (node, t) => {
  if (typeof node === 'string') return t(node);
  if (Array.isArray(node)) return node.map(child => translateNode(child, t));
  if (!isValidElement(node)) return node;
  if (node.props.translate === 'no') return node;
  const props = {};
  copyProps.forEach(key => { if (node.props[key] != null) props[key] = translateNode(node.props[key], t); });
  if (node.props.lines) props.lines = node.props.lines.map(line => ({ ...line, text: t(line.text) }));
  // Preserve single-child shape: Radix asChild/Slot requires one React element,
  // not the single-item array returned by React.Children.map.
  if (node.props.children != null) props.children = translateNode(node.props.children, t);
  return cloneElement(node, props);
};
export const L = ({ children }) => { const { t } = useLocale(); return translateNode(children, t); };